/**
 * <ReportChapterSectionCard> — one chapter block on the live report (organism, L0.1).
 *
 * One card per section: an assessment-linked block renders the `<ReportAssessmentHeader>` colored
 * banner followed by the preparation `<ProgressBar>` (+ % label) — mirroring the legacy widget's
 * header + progress rows; the orphan block renders a centered `<Title>` ("Avancement des chapitres").
 * Both then list their `<ReportChapterRow>`s. Pure composition of DS atoms/molecules. No hex.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Card } from "../../molecules/Card"
import { Stack } from "../../atoms/containers/Stack"
import { Title } from "../../atoms/typography/Title"
import { ReportAssessmentHeader } from "../../molecules/ReportAssessmentHeader"
import { ReportChapterRow } from "../../molecules/ReportChapterRow"
import { type UISchoolAssessment, type UILiveSessionReportChapter } from "../../../ports/primary/ui/types"
import { List } from "../../molecules/List"

export type ReportChapterSectionCardProps = {
    title: string
    assessment?: UISchoolAssessment
    chapters: UILiveSessionReportChapter[]
    testID?: string
}

export function ReportChapterSectionCard(props: ReportChapterSectionCardProps) {
    const { title, assessment, chapters, testID } = props

    if (chapters.length === 0) return null

    return (
        <Card p="0" testID={testID}>
            {assessment != null && <ReportAssessmentHeader assessment={assessment} />}
            <Stack gap="4" style={styles.content}>
                {!assessment && (
                    <Title center uppercase>
                        {title}
                    </Title>
                )}
                <Card p="4">
                    <List gap="8">
                        {chapters.map((chapter, index) => (
                            <ReportChapterRow key={index} chapter={chapter} />
                        ))}
                    </List>
                </Card>
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    content: {
        marginTop: theme.spacing["3"],
        padding: theme.spacing["4"],
    },
    bar: {
        flex: 1,
    },
}))
